import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { RefreshCw, LogOut, Calendar, Sun, Umbrella } from 'lucide-react';
import { Navigation } from './Navigation';

// SectionCard component
const SectionCard = ({ title, children }) => (
  <div className="bg-[#1A1A1A] rounded-lg p-4 card-shadow h-full">
    <h3 className="text-sm font-medium text-white mb-3 pb-2 border-b border-white/10">{title}</h3>
    {children}
  </div>
);

export const Ferieregnsskab = ({ user }) => {
  const navigate = useNavigate();
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [employeeData, setEmployeeData] = useState([]);
  const [totals, setTotals] = useState({ optjent: 0, afholdt: 0, resterende: 0, planlagt: 0 });
  const [year, setYear] = useState(new Date().getFullYear());

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/vacation/summary');
      setEmployeeData(response.data.employees || []);
      setTotals(response.data.totals || { optjent: 0, afholdt: 0, resterende: 0, planlagt: 0 });
      setYear(response.data.year || new Date().getFullYear());
    } catch (err) {
      console.error('Error fetching vacation data:', err);
      if (err.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
      navigate('/login');
    } catch (err) {
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen bg-[#121212]" data-testid="ferieregnsskab">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#121212]/95 backdrop-blur-md border-b border-white/5">
        <div className="max-w-[1920px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <Navigation />
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#1E6F7A]" />
                <h1 className="text-xl font-semibold text-white">Ferieregnsskab {year}</h1>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={handleRefresh}
                disabled={refreshing || loading}
                className="p-2 bg-[#1A1A1A] border border-white/10 text-white/60 hover:text-white rounded-lg transition-colors disabled:opacity-50"
                data-testid="refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={handleLogout}
                className="p-2 bg-[#1A1A1A] border border-white/10 text-white/60 hover:text-[#B3261E] rounded-lg transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1920px] mx-auto px-6 py-6 space-y-6">
        
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-white/60">Indlæser feriedata...</div>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-[#1A1A1A] rounded-lg p-4 border-l-4 border-[#1E6F7A]">
                <div className="flex items-center gap-2 mb-1">
                  <Sun className="w-4 h-4 text-white/40" />
                  <p className="text-xs text-white/60">Optjente feriedage</p>
                </div>
                <p className="text-2xl font-bold text-[#1E6F7A]">{totals.optjent}</p>
              </div>
              <div className="bg-[#1A1A1A] rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Umbrella className="w-4 h-4 text-white/40" />
                  <p className="text-xs text-white/60">Afholdte feriedage</p>
                </div>
                <p className="text-2xl font-bold text-[#2E7D32]">{totals.afholdt}</p>
              </div>
              <div className="bg-[#1A1A1A] rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="w-4 h-4 text-white/40" />
                  <p className="text-xs text-white/60">Resterende feriedage</p>
                </div>
                <p className="text-2xl font-bold text-[#C77700]">{totals.resterende}</p>
              </div>
              <div className="bg-[#1A1A1A] rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="w-4 h-4 text-white/40" />
                  <p className="text-xs text-white/60">Planlagte feriedage</p>
                </div>
                <p className="text-2xl font-bold text-white">{totals.planlagt}</p>
              </div>
            </div>

            {/* Employee Table */}
            <SectionCard title="Medarbejder oversigt">
              {employeeData.length === 0 ? (
                <div className="text-center py-8 text-white/60">
                  <p>Ingen feriedata fundet for {year}.</p>
                  <p className="text-sm mt-2">Tjek at kolonne S i Data_klar_1 indeholder "1" for feriedage.</p>
                </div>
              ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-white/60 border-b border-white/10">
                      <th className="text-left py-3 px-2">Medarbejder</th>
                      <th className="text-right py-3 px-2">Optjent</th>
                      <th className="text-right py-3 px-2">Afholdt</th>
                      <th className="text-right py-3 px-2">Resterende</th>
                      <th className="text-right py-3 px-2">Planlagt</th>
                      <th className="text-right py-3 px-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employeeData.map((emp, idx) => {
                      const uplanlagt = emp.resterende - emp.planlagt;
                      return (
                        <tr key={emp.name} className={`border-b border-white/5 ${idx % 2 === 0 ? 'bg-white/[0.02]' : ''}`}>
                          <td className="py-3 px-2 text-white font-medium">{emp.name}</td>
                          <td className="py-3 px-2 text-right text-white">{emp.optjent}</td>
                          <td className="py-3 px-2 text-right text-[#2E7D32]">{emp.afholdt}</td>
                          <td className="py-3 px-2 text-right text-[#C77700]">{emp.resterende}</td>
                          <td className="py-3 px-2 text-right text-white">{emp.planlagt}</td>
                          <td className="py-3 px-2 text-right">
                            {uplanlagt > 5 ? (
                              <span className="text-[#B3261E]">{uplanlagt} dage uplanlagt</span>
                            ) : uplanlagt > 0 ? (
                              <span className="text-[#C77700]">{uplanlagt} dage uplanlagt</span>
                            ) : (
                              <span className="text-[#2E7D32]">OK</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-white/20 font-bold">
                      <td className="py-3 px-2 text-white">Total</td>
                      <td className="py-3 px-2 text-right text-white">{totals.optjent}</td>
                      <td className="py-3 px-2 text-right text-[#2E7D32]">{totals.afholdt}</td>
                      <td className="py-3 px-2 text-right text-[#C77700]">{totals.resterende}</td>
                      <td className="py-3 px-2 text-right text-white">{totals.planlagt}</td>
                      <td className="py-3 px-2 text-right text-white/60">-</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              )}
            </SectionCard>
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 py-4 mt-8">
        <div className="max-w-[1920px] mx-auto px-6">
          <p className="text-center text-xs text-white/60">
            Luxsun Nordic · Ferieregnsskab · {new Date().getFullYear()}
          </p>
        </div>
      </footer>
    </div>
  );
};
